/**
 * HostPoint POS - Printer Management Components
 * UI for connecting and managing Bluetooth printers
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Printer, Bluetooth, BluetoothOff, Check, X, RefreshCw, Trash2, TestTube, Wifi, Settings2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { printerManager } from '@/lib/printer-manager';
import { cn } from '@/lib/utils';

/**
 * Printer status indicator for header
 */
export function PrinterIndicator({ onClick }) {
  const navigate = useNavigate();
  const [connectedCount, setConnectedCount] = useState(0);

  useEffect(() => {
    const updateCount = () => {
      setConnectedCount(printerManager.getConnectedPrinters().length);
    };
    
    updateCount();
    
    // Listen for connection changes
    printerManager.onConnectionChange = (event) => {
      updateCount();
      if (event.type === 'connected') {
        toast.success(`Printer connected: ${event.printer.name}`);
      } else if (event.type === 'disconnected') {
        toast.warning(`Printer disconnected: ${event.printer.name}`);
      }
    };

    return () => {
      printerManager.onConnectionChange = null;
    };
  }, []);

  const isConnected = connectedCount > 0;

  // If onClick is provided, use it; otherwise navigate to device management
  const handleClick = onClick || (() => navigate('/admin/devices?tab=hardware'));

  return (
    <button
      onClick={handleClick}
      className={cn(
        "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer",
        isConnected 
          ? "bg-emerald-500/10 text-emerald-500" 
          : "bg-muted text-muted-foreground hover:bg-muted/80"
      )}
    >
      <Printer className="h-3.5 w-3.5" />
      {isConnected ? (
        <span>{connectedCount}</span>
      ) : (
        <span>No Printer</span>
      )}
    </button>
  );
}

/**
 * Printer management dialog
 */
export function PrinterManagerDialog({ open, onOpenChange }) {
  const [isScanning, setIsScanning] = useState(false);
  const [connectedPrinters, setConnectedPrinters] = useState([]);
  const [savedPrinters, setSavedPrinters] = useState([]);
  const [isSupported, setIsSupported] = useState(true);
  const [newPrinterType, setNewPrinterType] = useState('receipt');
  const [newPrinterWidth, setNewPrinterWidth] = useState('58');

  const refreshPrinters = useCallback(() => {
    setConnectedPrinters(printerManager.getConnectedPrinters());
    setSavedPrinters(printerManager.getSavedPrinters());
    setIsSupported(printerManager.isSupported());
  }, []);

  useEffect(() => {
    if (open) {
      refreshPrinters();
    }
  }, [open, refreshPrinters]);

  const handleScanAndConnect = async () => {
    if (!isSupported) {
      toast.error('Web Bluetooth is not supported in this browser');
      return;
    }

    setIsScanning(true);
    try {
      await printerManager.scanAndConnect({
        type: newPrinterType,
        paperWidth: parseInt(newPrinterWidth)
      });
      refreshPrinters();
    } catch (error) {
      if (!error.message.includes('cancelled')) {
        toast.error(error.message || 'Failed to connect to printer');
      }
    } finally {
      setIsScanning(false);
    }
  };

  const handleDisconnect = (printerId) => {
    printerManager.disconnect(printerId);
    refreshPrinters();
    toast.info('Printer disconnected');
  };

  const handleRemoveSaved = (printerId) => {
    printerManager.removeSavedPrinter(printerId);
    refreshPrinters();
    toast.info('Printer removed from saved list');
  };

  const handleTestPrint = async (printerId) => {
    try {
      await printerManager.testPrint(printerId);
      toast.success('Test page printed');
    } catch (error) {
      toast.error(error.message || 'Test print failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bluetooth className="h-5 w-5 text-blue-500" />
            Bluetooth Printers
          </DialogTitle>
          <DialogDescription>
            Connect and manage receipt and label printers via Bluetooth
          </DialogDescription>
        </DialogHeader>

        {!isSupported && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-500">
            <div className="flex items-center gap-2">
              <BluetoothOff className="h-4 w-4" />
              <span className="font-medium">Bluetooth Not Supported</span>
            </div>
            <p className="mt-1 text-xs">
              Web Bluetooth requires Chrome on Android or a compatible desktop browser.
            </p>
          </div>
        )}

        {/* Connected Printers */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Wifi className="h-4 w-4 text-emerald-500" />
            Connected Printers
          </h4>
          
          {connectedPrinters.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground text-sm">
              No printers connected
            </div>
          ) : (
            <div className="space-y-2">
              {connectedPrinters.map((printer) => (
                <div 
                  key={printer.id}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/10 rounded-lg">
                      <Printer className="h-4 w-4 text-emerald-500" />
                    </div>
                    <div>
                      <div className="font-medium text-sm">{printer.name}</div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline" className="text-xs">
                          {printer.type}
                        </Badge>
                        <span>{printer.paperWidth}mm</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleTestPrint(printer.id)}
                      title="Test Print"
                    >
                      <TestTube className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDisconnect(printer.id)}
                      className="text-red-500 hover:text-red-600"
                      title="Disconnect"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add New Printer */}
        <div className="space-y-3 pt-3 border-t">
          <h4 className="text-sm font-medium">Add Printer</h4>
          
          {/* iMin Internal Printer Quick Connect */}
          {printerManager.hasIminSDK() && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-blue-700 dark:text-blue-400">
                    iMin Internal Printer Detected
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Use built-in thermal printer (recommended)
                  </div>
                </div>
                <Button 
                  size="sm"
                  onClick={async () => {
                    try {
                      setIsScanning(true);
                      await printerManager.connectIminInternal({
                        type: newPrinterType,
                        paperWidth: parseInt(newPrinterWidth) || 80
                      });
                      refreshPrinters();
                      toast.success('iMin internal printer connected');
                    } catch (error) {
                      toast.error(error.message || 'Failed to connect');
                    } finally {
                      setIsScanning(false);
                    }
                  }}
                  disabled={isScanning}
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Connect
                </Button>
              </div>
            </div>
          )}

          {/* iMin Bluetooth Printer (BluetoothPrinter) - for V2 devices */}
          {printerManager.isIminDevice() && !printerManager.hasIminSDK() && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              <div className="text-sm text-amber-700 dark:text-amber-400">
                <strong>iMin Device Detected</strong> - SDK not loaded
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Ensure iMinPrinterPlugin is installed in your WebView app. 
                The built-in Bluetooth printer name is &quot;BluetoothPrinter&quot;.
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Printer Type</Label>
              <Select value={newPrinterType} onValueChange={setNewPrinterType}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="receipt">Receipt Printer</SelectItem>
                  <SelectItem value="label">Label Printer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-xs">Paper Width</Label>
              <Select value={newPrinterWidth} onValueChange={setNewPrinterWidth}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="58">58mm (32 chars)</SelectItem>
                  <SelectItem value="80">80mm (48 chars)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button 
            onClick={handleScanAndConnect} 
            disabled={isScanning || !isSupported}
            className="w-full"
          >
            {isScanning ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Bluetooth className="h-4 w-4 mr-2" />
                Scan for Bluetooth Printers
              </>
            )}
          </Button>
        </div>

        {/* Saved Printers */}
        {savedPrinters.length > 0 && (
          <div className="space-y-3 pt-3 border-t">
            <h4 className="text-sm font-medium text-muted-foreground">Previously Connected</h4>
            <div className="space-y-2">
              {savedPrinters
                .filter(saved => !connectedPrinters.find(c => c.id === saved.id))
                .map((printer) => (
                  <div 
                    key={printer.id}
                    className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <Printer className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground">{printer.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {printer.type}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveSaved(printer.id)}
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500"
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
            </div>
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              onOpenChange(false);
              window.location.href = '/admin/devices?tab=hardware';
            }}
            className="sm:mr-auto"
          >
            <Settings2 className="h-4 w-4 mr-2" />
            Device Management
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Quick print button with printer selection
 */
export function PrintButton({ 
  onPrint, 
  label = "Print", 
  printerType = "receipt",
  disabled = false,
  variant = "outline",
  size = "default"
}) {
  const [isPrinting, setIsPrinting] = useState(false);

  const handlePrint = async () => {
    const printer = printerManager.getPrinterByType(printerType);
    
    if (!printer) {
      toast.error(`No ${printerType} printer connected. Please connect a printer first.`);
      return;
    }

    setIsPrinting(true);
    try {
      await onPrint(printer.id);
      toast.success('Printed successfully');
    } catch (error) {
      toast.error(error.message || 'Print failed');
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handlePrint}
      disabled={disabled || isPrinting}
    >
      {isPrinting ? (
        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <Printer className="h-4 w-4 mr-2" />
      )}
      {label}
    </Button>
  );
}

export default {
  PrinterIndicator,
  PrinterManagerDialog,
  PrintButton
};
